import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HealthDeclarationService } from '../../shared/services/health-declaration.service';
import { ToastrService } from 'ngx-toastr';
import { CompanyService } from '../../shared/services/company.service';
import { QuoteService } from '../../shared/services/quote.service';
import { QuotaTriggerService } from '../../shared/services/quota-trigger.service';
import { cloneDeep, isEqual } from 'lodash';
import { TranslateModule } from '@ngx-translate/core';

type SeverityLevel = 'Mild' | 'Moderate' | 'Severe';

interface SubCondition {
  name: string;
  selected: boolean;
  severity: string | null;
  severityOptions?: string[];
}

interface HealthQuestion {
  key: string;
  label: string;
  selected: boolean;
  type: 'input' | 'disease-selection';
  detailsLabel?: string;
  reason?: string;
  reasonValues?: string[];
  detailsValue?: string | null;
  conditions?: SubCondition[];
}

interface Member {
  id: string;
  idNumber: string;
  name: string;
  dob: string;
  type: 'Employee' | 'Dependent';
  gender: 'Male' | 'Female';
  hasDeclaration: boolean;
}

interface PregnancyQuestion {
  label: string;
  options: string[];
  pregenancy: boolean;
  selectedValue: string;
  dueDate: string | null;
}

interface DeclarationData {
  height: number | null;
  weight: number | null;
  hospitalization: boolean;
  numberOfTimesHospitalized: number | null;
  reasonOfHospitalization: string;
  healthQuestions: HealthQuestion[];
  expectedDeliveryDate: string | null;
  pregnencyCondition: string | null;
  pregnent: boolean;
}

interface BackendCondition {
  selected: boolean;
  name: string;
  severity: string | null;
}

interface BackendHealthQuestionsDto {
  key: string;
  selected: boolean;
  conditions?: BackendCondition[];
}

interface BackendHealthDeclarationDTO {
  height: number | null;
  weight: number | null;
  numberOfTimesHospitalized: number | null;
  hospitalization: boolean;
  reasonOfHospitalization: string;
  isPregnent: boolean;
  pregnencyCondition: string;
  expectedDeliveryDate: string | null;
  healthQuestions: BackendHealthQuestionsDto[];
}

@Component({
  selector: 'app-health-declaration-popup',
  templateUrl: './health-declaration-popup.component.html',
  styleUrls: ['./health-declaration-popup.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule, TranslateModule],
})
export class HealthDeclarationPopupComponent implements OnInit {
  @Input() member!: Member;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  height: number | null = null;
  weight: number | null = null;
  hospitalization: boolean = false;
  hospitalizationCount: number | null = null;
  hospitalizationReason: string = '';
  isPregnant: boolean = false;
  pregnancyQuestionSelectedValue: string = '';
  pregnancyDueDate: string | null = null;

  hospitalizationLabel: string =
    'Any hospital admission during the last 12 months, admission, registering as an out-patient/patient in the hospital until the following month?';
  healthQuestions: HealthQuestion[] = [
    {
      key: 'diagnosedDiseaseCategories',
      label:
        "Have you been diagnosed with any of the following chronic diseases Limited to: Autism, Listed Benign Tumor (Breast tumors, fibroid uterus, benign prostatic hyperplasia, thyroid goiter and parathyroid glands, liver tumors, colon tumors), Malignant tumors, Listed Cardiac diseases (coronary and valve heart disease, heart failure, cardiac fibrillation, myocardial infarction, heart clots). Chronic Hepatitis C, Gallstones, Sever Kidney failure (stage 5 Requiring dialysis, clearance of less than 15 ml/minute*), Urinary tract stones, hernias, Autoimmune diseases (lupus, rheumatoid arthritis, psoriasis, Chron's disease, ulcerative colitis, multiple sclerosis, celiac disease)",
      selected: false,
      type: 'disease-selection',
      conditions: [
        {
          name: 'Autism',
          selected: false,
          severity: null,
          severityOptions: ['Autism type 1', 'Autism type 2', 'Autism type 3'],
        },
        {
          name: 'Benign Tumor',
          selected: false,
          severity: null,
          severityOptions: [
            'Benign Tumor 1',
            'Benign Tumor 2',
            'Benign Tumor 3',
          ],
        },
        {
          name: 'Cancer',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Heart Diseases',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Chronic Hepatitis C',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Gallstones',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Kidney Failure',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Urinary Tract Stones',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Thyroid goiter ',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Cysts',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'fibroid uterus',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Hernias',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'autoimmune diseases',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Multiple sderosis',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
      ],
    },
    {
      key: 'congenitalDisorder',
      label:
        "Have you been diagnosed with any of the following congenital disorder or hereditary diseases limited to: Cerebral palsy, Sickle cell disorder, Thalassemia, hemophilia, metabolic diseases, Hydrocephalus, spinal muscle atrophy, genital malformations, Chromosomal abnormalities, Gaucher's disease, G6PD Deficiency, cystic fibrosis, hemochromatosis, Wilson disease, Polycystic Kidney Disease",
      selected: false,
      type: 'disease-selection',
      conditions: [
        {
          name: 'Cerebral palsy',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Sickle cell disorder',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Thalassemia',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'hemophilia',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'metabolic diseases',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Hydrocephalus',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Spinal muscle atrophy',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'genital malformations',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Chromosomal abnormalities',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Gaucher’s disease',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'G6PD Deficiency',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Systic fibrosis',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Hemochromatosis',
          selected: false,
          severity: null,
          severityOptions: ['Minor', 'Med', 'Major'],
        },
        {
          name: 'Wilson disease',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Polycystic kidney disease',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
      ],
    },
    {
      key: 'visuaLDisorder',
      label:
        'Have you been diagnosed with any of the following eye diseases limited to: Cataract, Glaucoma, Corneal diseases or Retinal diseases.',
      selected: false,
      type: 'disease-selection',
      conditions: [
        {
          name: 'Cataract',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Glaucoma',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Corneal diseases',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Retinal diseases',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
      ],
    },
    {
      key: 'boneDisease',
      label:
        'Have you been diagnosed with any of the following bone diseases limited to: Vertebral disc prolapse (moderate or severe), Scoliosis (moderate or severe)**, Ligament tears, osteoarthritis (moderate or severe).',
      selected: false,
      type: 'disease-selection',
      conditions: [
        {
          name: 'Vertebral disc prolapse',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Scoliosis',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Arthritis',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
        {
          name: 'Ligament tears',
          selected: false,
          severity: null,
          severityOptions: ['Mild', 'Moderate', 'Severe'],
        },
      ],
    },
  ];

  pregnancyQuestionInit: any = {
    label: 'Pregnancy?',
    options: [
      'Currently single pregnancy',
      'Currently multiple pregnancy',
      'Currently single pregnancy with previous C/S delivery',
    ],
    selectedValue: '',
    dueDate: null,
  };

  private initialData: DeclarationData | null = null;
  private isExistingDeclaration = false;
  private companyId: number | undefined;
  declarationData: any;
  private dataFound: boolean = false;

  constructor(
    private healthService: HealthDeclarationService,
    private toastr: ToastrService,
    private companyService: CompanyService,
    private quotaTriggerService: QuotaTriggerService
  ) {}

  ngOnInit(): void {
    this.companyId = this.companyService.getCompanyDetails()?.id || 22;
    this.fetchHealthDeclaration();
  }

  onHospitalizationToggle() {
    this.hospitalization = !this.hospitalization;
    if (!this.hospitalization) {
      this.hospitalizationCount = null;
      this.hospitalizationReason = '';
    }
  }

  fetchHealthDeclaration(): void {
    this.healthService
      .getHealthDeclaration(this.member.idNumber, this.member.type)
      .subscribe({
        next: (data: { healthDeclaration: DeclarationData }) => {
          this.isExistingDeclaration = true;
          this.populateForm(data.healthDeclaration);
          this.initialData = cloneDeep({
            height: this.height,
            weight: this.weight,
            hospitalization: this.hospitalization,
            numberOfTimesHospitalized: this.hospitalizationCount,
            reasonOfHospitalization: this.hospitalizationReason,
            healthQuestions: cloneDeep(this.healthQuestions),
            pregnent: this.isPregnant,
            pregnencyCondition: this.pregnancyQuestionSelectedValue,
            expectedDeliveryDate: this.pregnancyDueDate,
          });
        },
        error: (err) => {
          this.isExistingDeclaration = false;
          if (err.status === 404) {
            this.dataFound = false;
            this.initialData = cloneDeep({
              height: this.height,
              weight: this.weight,
              hospitalization: this.hospitalization,
              numberOfTimesHospitalized: this.hospitalizationCount,
              reasonOfHospitalization: this.hospitalizationReason,
              healthQuestions: cloneDeep(this.healthQuestions),
              pregnencyCondition: this.pregnancyQuestionSelectedValue,
              expectedDeliveryDate: this.pregnancyDueDate,
              pregnent: this.isPregnant,
            });
          } else {
            this.toastr.error('Failed to load declaration data.');
          }
        },
      });
  }

  private populateForm(data: DeclarationData): void {
    this.height = data.height;
    this.weight = data.weight;
    this.hospitalization = data.hospitalization || false;
    this.hospitalizationCount = data.numberOfTimesHospitalized;
    this.hospitalizationReason = data.reasonOfHospitalization;
    this.isPregnant = data.pregnent || false;
    this.pregnancyQuestionSelectedValue = data.pregnencyCondition || '';
    this.pregnancyDueDate = data.expectedDeliveryDate || null;
    if (data.healthQuestions) {
      data.healthQuestions.forEach((fetchedQuestion) => {
        const formQuestion = this.healthQuestions.find(
          (q) => q.key === fetchedQuestion.key
        );
        if (formQuestion) {
          formQuestion.selected = fetchedQuestion.selected;
          formQuestion.detailsValue = fetchedQuestion.detailsValue || null;
          formQuestion.reason = fetchedQuestion.reason || undefined;
          formQuestion.reasonValues = fetchedQuestion.reasonValues || undefined;

          if (formQuestion.conditions && fetchedQuestion.conditions) {
            fetchedQuestion.conditions.forEach((fetchedCondition) => {
              const formCondition = formQuestion.conditions?.find(
                (c) => c.name === fetchedCondition.name
              );
              if (formCondition) {
                formCondition.selected = fetchedCondition.selected;
                formCondition.severity = fetchedCondition.severity || null;
              }
            });
          }
        }
      });
    }
  }

  private mapToBackendDto(): BackendHealthDeclarationDTO {
    return {
      height: this.height,
      weight: this.weight,
      numberOfTimesHospitalized: this.hospitalizationCount,
      hospitalization: this.hospitalization,
      reasonOfHospitalization: this.hospitalizationReason,
      isPregnent: this.isPregnant,
      pregnencyCondition: this.pregnancyQuestionSelectedValue,
      expectedDeliveryDate: this.pregnancyDueDate,
      healthQuestions: this.healthQuestions.map((q) => ({
        key: q.key,
        selected: q.selected,
        conditions: q.conditions?.map((cond) => ({
          selected: cond.selected,
          name: cond.name,
          severity: cond.severity,
        })),
      })),
    };
  }

  onSave(): void {
    if (this.isExistingDeclaration) {
      const patchData = this.buildPatchObject();

      if (
        Object.keys(patchData).length === 0 ||
        (Object.keys(patchData).length === 1 &&
          patchData.healthQuestions &&
          patchData.healthQuestions.length === 0)
      ) {
        this.toastr.info('No changes were made.');
        return;
      }

      this.healthService
        .patchHealthDeclaration(
          this.member.idNumber,
          this.member.type,
          patchData
        )
        .subscribe({
          next: () => {
            this.toastr.success('Health declaration updated successfully!');
            this.save.emit(patchData);
            
            // Mark that health declaration was updated for this company
            const companyId = this.companyService.getCompanyDetails()?.id?.toString();
            console.log('Health declaration updated - marking for companyId:', companyId);
            if (companyId) {
              this.quotaTriggerService.markHealthDeclarationUpdated(companyId);
            } else {
              console.warn('No companyId found - cannot mark health declaration update');
            }
            
            this.close.emit();
          },
          error: (err) => {
            this.toastr.error('Failed to update health declaration.');
          },
        });
    } else {
      const postData = this.mapToBackendDto();
      this.healthService
        .postHealthDeclaration(this.member.idNumber, this.member.type, postData)
        .subscribe({
          next: () => {
            this.toastr.success('Health declaration saved successfully!');
            this.save.emit(postData);
            
            // Mark that health declaration was updated for this company
            const companyId = this.companyService.getCompanyDetails()?.id?.toString();
            console.log('Health declaration saved - marking for companyId:', companyId);
            if (companyId) {
              this.quotaTriggerService.markHealthDeclarationUpdated(companyId);
            } else {
              console.warn('No companyId found - cannot mark health declaration update');
            }
            
            this.close.emit();
          },
          error: (err) => {
            this.toastr.error('Failed to save health declaration.');
          },
        });
    }
  }

  private buildPatchObject(): Partial<BackendHealthDeclarationDTO> {
    const patchData: Partial<BackendHealthDeclarationDTO> = {};
    if (!this.initialData) return {};

    if (this.height !== this.initialData.height) {
      patchData.height = this.height;
    }
    if (this.weight !== this.initialData.weight) {
      patchData.weight = this.weight;
    }
    if (this.hospitalization !== this.initialData.hospitalization) {
      patchData.hospitalization = this.hospitalization;
    }
    if (
      this.hospitalizationCount !== this.initialData.numberOfTimesHospitalized
    ) {
      patchData.numberOfTimesHospitalized = this.hospitalizationCount;
    }
    if (
      this.hospitalizationReason !== this.initialData.reasonOfHospitalization
    ) {
      patchData.reasonOfHospitalization = this.hospitalizationReason;
    }

    if (this.isPregnant !== this.initialData.pregnent) {
      patchData.isPregnent = this.isPregnant;
      patchData.pregnencyCondition = this.pregnancyQuestionSelectedValue;
      patchData.expectedDeliveryDate = this.pregnancyDueDate;
    }

    const changedHealthQuestions: BackendHealthQuestionsDto[] = [];

    this.healthQuestions.forEach((currentQuestion) => {
      const initialQuestion = this.initialData!.healthQuestions.find(
        (q) => q.key === currentQuestion.key
      );

      if (!initialQuestion) {
        if (
          currentQuestion.selected ||
          (currentQuestion.conditions &&
            currentQuestion.conditions.some((c) => c.selected))
        ) {
          changedHealthQuestions.push(
            this.mapQuestionToBackend(currentQuestion)
          );
        }
        return;
      }

      const questionChanged =
        currentQuestion.selected !== initialQuestion.selected;
      const detailsChanged =
        currentQuestion.detailsValue !== initialQuestion.detailsValue;
      const reasonChanged = currentQuestion.reason !== initialQuestion.reason;

      if (
        initialQuestion.selected ||
        currentQuestion.selected ||
        questionChanged ||
        detailsChanged ||
        reasonChanged
      ) {
        changedHealthQuestions.push(this.mapQuestionToBackend(currentQuestion));
      } else {
        const changedConditions = this.getChangedConditions(
          currentQuestion,
          initialQuestion
        );
        if (changedConditions.length > 0) {
          changedHealthQuestions.push({
            key: currentQuestion.key,
            selected: currentQuestion.selected,
            conditions: changedConditions,
          });
        }
      }
    });

    if (changedHealthQuestions.length > 0) {
      patchData.healthQuestions = changedHealthQuestions;
    }

    return patchData;
  }

  private mapQuestionToBackend(
    question: HealthQuestion
  ): BackendHealthQuestionsDto {
    return {
      key: question.key,
      selected: question.selected,
      conditions: question.conditions?.map((cond) => ({
        selected: cond.selected,
        name: cond.name,
        severity: cond.severity,
      })),
    };
  }

  private getChangedConditions(
    currentQuestion: HealthQuestion,
    initialQuestion: HealthQuestion
  ): BackendCondition[] {
    const changedConditions: BackendCondition[] = [];

    if (!currentQuestion.conditions || !initialQuestion.conditions) {
      return changedConditions;
    }

    currentQuestion.conditions.forEach((currentCond) => {
      const initialCond = initialQuestion.conditions?.find(
        (c) => c.name === currentCond.name
      );

      if (currentCond.selected || (initialCond && initialCond.selected)) {
        changedConditions.push({
          selected: currentCond.selected,
          name: currentCond.name,
          severity: currentCond.severity,
        });
      }
    });

    return changedConditions;
  }

  onClose(): void {
    this.close.emit();
  }

  onMainToggleChange(question: HealthQuestion): void {
    question.selected = !question.selected;
    if (!question.selected) {
      if (question.type === 'input') {
        question.detailsValue = null;
      } else if (question.type === 'disease-selection' && question.conditions) {
        question.conditions.forEach((c) => {
          c.selected = false;
          c.severity = null;
        });
      }
    }
  }

  onConditionToggle(condition: SubCondition): void {
    condition.selected = !condition.selected;
    if (!condition.selected) {
      condition.severity = null;
    }
  }

  onSeverityChange(condition: SubCondition, severity?: string): void {
    if (!condition.selected) return;
    condition.severity = severity || null;
  }

  onRadioChange(value: string): void {
    this.pregnancyQuestionSelectedValue = value;
  }

  onPregnancyToggle(): void {
    this.isPregnant = !this.isPregnant;
    if (!this.isPregnant) {
      this.pregnancyQuestionSelectedValue = '';
      this.pregnancyDueDate = null;
    }
  }
}
